import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Layers, Globe, Zap } from 'lucide-react';

const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const Partners = () => {
  const benefits = [
    { icon: Layers, title: "API-First Integration", desc: "Easily embed our metrics into your existing software dashboards." },
    { icon: Globe, title: "Co-Selling Support", desc: "Get access to our sales collateral, lead sharing, and demo support." },
    { icon: Zap, title: "Rapid ROI", desc: "Proven impact that justifies your software or hardware renewal costs." }
  ];

  const journey = [
    { step: "01", title: "Integrate", desc: "Connect via API or Webhooks to start ingesting Vision AI metadata." },
    { step: "02", title: "Co-Sell", desc: "Identify key accounts for joint pilots and demonstration of value." },
    { step: "03", title: "Scale", desc: "Roll out to the enterprise fleet with priority tiered support." }
  ];

  return (
    <div data-testid="partners-page" className="bg-white">
      {/* Hero */}
      <section className="py-20 bg-foreground text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent"></div>
        <div className="container-custom relative z-10 text-center">
          <AnimatedSection>
            <h1 className="text-4xl lg:text-6xl font-extrabold font-heading mb-6">
              Build the <span className="text-gradient">Future of Retail</span> Together.
            </h1>
            <p className="text-xl text-white/70 max-w-2xl mx-auto">
              We partner with POS providers, ERP vendors, and System Integrators to bring Vision AI to physical commerce globally.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Benefits & Journey */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            <AnimatedSection>
              <div>
                <h2 className="text-3xl font-bold font-heading mb-10">Why Partner with Us?</h2>
                <div className="space-y-8">
                  {benefits.map((benefit, i) => (
                    <div key={i} className="flex" data-testid={`benefit-${i}`}>
                      <div className="flex-shrink-0 w-12 h-12 bg-secondary rounded-xl flex items-center justify-center text-primary mr-4">
                        <benefit.icon size={24} />
                      </div>
                      <div>
                        <h4 className="font-bold text-foreground text-lg mb-1">{benefit.title}</h4>
                        <p className="text-muted-foreground text-sm leading-relaxed">{benefit.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
            
            <AnimatedSection delay={0.2}>
              <div className="bg-secondary/50 p-10 rounded-2xl border border-border">
                <h3 className="text-2xl font-bold font-heading mb-8">Partner Journey</h3>
                <div className="space-y-10">
                  {journey.map((item, i) => (
                    <div key={i} className="flex items-start" data-testid={`journey-step-${i}`}>
                      <div className="text-4xl font-extrabold text-border mr-6 font-heading">{item.step}</div>
                      <div>
                        <h4 className="font-bold text-foreground mb-1">{item.title}</h4>
                        <p className="text-muted-foreground text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-secondary/30">
        <div className="container-custom">
          <AnimatedSection>
            <div className="bg-white p-12 rounded-2xl border border-border text-center max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold font-heading text-foreground mb-6">Inquire About Our Partner Program</h2>
              <p className="text-muted-foreground mb-8 italic">
                "Adding Vision AI to our ERP stack reduced our customer churn by 12% in the first year." — Managing Director, Enterprise POS Vendor
              </p>
              <Link 
                to="/contact" 
                data-testid="partner-cta"
                className="bg-primary text-white px-8 py-4 rounded-full font-bold hover:bg-primary/90 transition-all"
              >
                Apply to Partner
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default Partners;
